import React, { useState, useEffect, useRef } from 'react';
import PermissionGuard from '../../../common/PermissionGuard';
import { createPortal } from 'react-dom';
import { PlusIcon, SearchIcon, PencilIcon, EyeIcon, EyeOffIcon, TrashIcon } from '@heroicons/react/outline';
import { useNavigate } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { DotsVerticalIcon } from '@heroicons/react/solid';
import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { toastService } from '../../../../services/toast';
import DeleteConfirmModal from '../../../common/DeleteConfirmModal';
import PublishConfirmModal from '../../../common/PublishConfirmModal';
import { coursesApi } from '../../../../services/api/courses.api';
import { BASE_URL } from '../../../../config/axios';

// مكونات الصفحة
import FilterSection from './components/FilterSection';
import SearchBar from './components/SearchBar';

const DropdownMenu = ({ isOpen, onClose, style, children }) => {
  if (!isOpen) return null;

  return createPortal(
    <div 
      className="fixed z-50 bg-white dark:bg-gray-800 rounded-md shadow-lg w-48 ring-1 ring-black ring-opacity-5 focus:outline-none"
      style={style}
      role="menu"
      aria-orientation="vertical"
      aria-labelledby="menu-button"
      tabIndex="-1"
    >
      {children}
    </div>,
    document.body
  );
};

const CoursesListPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedInstructor, setSelectedInstructor] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const menuRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const coursesData = await coursesApi.getAll();
        const categoriesData = await coursesApi.getCategories();
        const instructorsData = await coursesApi.getInstructors();
        
        setCourses(coursesData || []);
        setCategories(categoriesData || []);
        setInstructors(instructorsData || []);
      } catch (error) {
        toastService.error('حدث خطأ أثناء تحميل البيانات');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // تصفية الدورات حسب البحث والفلاتر
  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         course.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = selectedStatus === 'all' || 
                         (selectedStatus === 'published' && course.status === 'published') ||
                         (selectedStatus === 'draft' && course.status === 'draft');
    
    // تصفية حسب المدرب (إذا كان متاحًا)
    const matchesInstructor = selectedInstructor === 'all' || 
                             (course.instructors && course.instructors.some(instructor => 
                               instructor.id === parseInt(selectedInstructor)));
    
    // تصفية حسب التصنيف
    const matchesCategory = selectedCategory === 'all' || 
                           course.category_id === parseInt(selectedCategory);

    return matchesSearch && matchesStatus && matchesInstructor && matchesCategory;
  });

  const handleDelete = (course) => {
    setSelectedCourse(course);
    setDeleteDialogOpen(true);
  };

  const handlePublishToggle = (course) => {
    setSelectedCourse(course);
    setPublishDialogOpen(true);
  };

  const handleEdit = (course) => {
    navigate(`/admin/dashboard/courses/edit/${course.id}`);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedCourse) return;
    
    try {
      await coursesApi.remove(selectedCourse.id);
      setCourses(courses.filter(course => course.id !== selectedCourse.id));
      setDeleteDialogOpen(false);
      toastService.success(`تم حذف الدورة "${selectedCourse.name}" بنجاح`);
    } catch (error) {
      toastService.error('حدث خطأ أثناء حذف الدورة');
    }
  };

  const handlePublishConfirm = async () => {
    if (!selectedCourse) return;
    
    try {
      const newStatus = selectedCourse.status === 'published' ? 'draft' : 'published';
      await coursesApi.updateStatus(selectedCourse.id, newStatus);
      
      setCourses(courses.map(course => 
        course.id === selectedCourse.id 
          ? { ...course, status: newStatus } 
          : course
      ));
      
      setPublishDialogOpen(false);
      
      if (newStatus === 'published') {
        toastService.success(`تم نشر الدورة "${selectedCourse.name}" بنجاح`);
      } else {
        toastService.success(`تم إلغاء نشر الدورة "${selectedCourse.name}" بنجاح`);
      }
    } catch (error) {
      toastService.error('حدث خطأ أثناء تحديث حالة الدورة');
    }
  };

  const handleMenuClick = (event, courseId) => {
    const buttonRect = event.currentTarget.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const spaceBelow = windowHeight - buttonRect.bottom;
    const menuHeight = 150; // تقريباً ارتفاع القائمة بالبكسل
    const shouldShowAbove = spaceBelow < menuHeight;

    setMenuPosition({
      top: shouldShowAbove ? 'auto' : buttonRect.bottom + window.scrollY,
      bottom: shouldShowAbove ? (windowHeight - buttonRect.top + window.scrollY) : 'auto',
      left: buttonRect.left,
    });
    setOpenMenuId(openMenuId === courseId ? null : courseId);
  };

  const getStatusBadge = (course) => {
    // إذا كانت مسودة
    if (course.status === 'draft') {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">مسودة</span>;
    }
    
    // إذا كانت منشورة
    if (course.status === 'published') {
      // لا نتحقق من تاريخ البدء، نعرض الحالة كما هي في قاعدة البيانات
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">منشورة</span>;
    }
    
    // حالة افتراضية (لا ينبغي الوصول إليها)
    return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">غير معروفة</span>;
  };

  // تنسيق التاريخ بالشكل المطلوب
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.getMonth() + 1; // الشهور تبدأ من 0
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // تحويل مسار الصورة إلى مسار كامل
  const getImageUrl = (path) => {
    // إذا كان المسار فارغاً
    if (!path) return 'https://placehold.co/600x400/e2e8f0/1e293b.png?text=Course';
    
    // إذا كان المسار يبدأ بـ http أو https، نستخدمه كما هو
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    
    // إذا كان المسار يبدأ بـ /storage، نضيف عنوان الباك إند
    if (path.startsWith('/storage')) {
      return `${BASE_URL}${path}`;
    }
    
    // إذا كان المسار عبارة عن رقم فقط (صور المدربين الافتراضية)
    if (/^\d+$/.test(path)) {
      return `/images/avatars/avatar${path}.png`;
    }
    
    // في الحالات الأخرى، نفترض أنه مسار نسبي ونضيف عنوان الباك إند
    return `${BASE_URL}/storage/${path}`;
  };

  return (
    <div className="p-6">
      {/* بطاقة البحث والفلترة */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg mb-6">
        {/* رأس البحث */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">قائمة الدورات</h2>
          <div className="flex gap-4">
            <PermissionGuard permission="create_course">
              <div className="relative group">
                <PlusIcon 
                  className="h-6 w-6 text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400 cursor-pointer transition-colors duration-200"
                  onClick={() => navigate('/admin/dashboard/courses/create')}
                />
                <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 -left-4 -translate-x-1/2 mt-2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-50 transition-all duration-75">
                  إضافة دورة جديدة
                </div>
              </div>
            </PermissionGuard>
          </div>
        </div>
        
        {/* محتوى البحث */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* حقل البحث */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">البحث عن دورة</label>
              <div className="relative rounded-lg">
                <input
                  type="text"
                  placeholder="البحث عن دورة..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input-base pr-10"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <SearchIcon className="h-5 w-5 text-blue-500 dark:text-blue-400" />
                </div>
              </div>
            </div>

            {/* فلتر الحالة */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">الحالة</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="input-base"
              >
                <option value="all">جميع الحالات</option>
                <option value="published">منشورة</option>
                <option value="draft">مسودة</option>
              </select>
            </div>

            {/* فلتر المدرب */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">المدرب</label>
              <select
                value={selectedInstructor}
                onChange={(e) => setSelectedInstructor(e.target.value)}
                className="input-base"
              >
                <option value="all">جميع المدربين</option>
                {instructors.map(instructor => (
                  <option key={instructor.id} value={instructor.id}>{instructor.name}</option>
                ))}
              </select>
            </div>

            {/* فلتر التصنيف */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">التصنيف</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="input-base"
              >
                <option value="all">جميع التصنيفات</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* جدول الدورات */}
      <div className="mt-6 overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th scope="col" className="px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  الدورة
                </th>
                <th scope="col" className="px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100 text-center">
                  المدرب
                </th>
                <th scope="col" className="px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100 text-center">
                  الحالة
                </th>
                <th scope="col" className="px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100 text-center">
                  السعر
                </th>
                <th scope="col" className="w-40 px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100 text-center">
                  تاريخ البداية
                </th>
                <th scope="col" className="w-32 px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  الإجراءات
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                    <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">جاري تحميل البيانات...</div>
                  </td>
                </tr>
              ) : filteredCourses.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <svg className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">لا توجد دورات متاحة</div>
                  </td>
                </tr>
              ) : (
                filteredCourses.map((course) => (
                  <tr key={course.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          <img className="h-10 w-10 rounded object-cover" src={getImageUrl(course.cover_image)} alt="" />
                        </div>
                        <div className="mr-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {course.name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {course.category?.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center justify-center">
                        {course.instructors && course.instructors.map(instructor => {
                        if (instructor.image) {
                          return <img key={instructor.id} className="h-8 w-8 rounded-full" src={getImageUrl(instructor.image)} alt={instructor.name} />;
                        } else {
                          const avatarId = (instructor.id % 8) + 1;
                          return <img key={instructor.id} className="h-8 w-8 rounded-full" src={`/images/avatars/avatar${avatarId}.png`} alt={instructor.name} />;
                        }
                      })}
                        <div className="mr-2 text-sm text-gray-900 dark:text-white">
                          {course.instructors && course.instructors.map(instructor => instructor.name).join(', ')}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {getStatusBadge(course)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm text-gray-900 dark:text-white">{course.price ? `${parseInt(course.price)} ريال` : 'مجاني'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {course.start_date ? formatDate(course.start_date) : 'غير محدد'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium space-x-2 space-x-reverse">
                      <PermissionGuard permission="edit_course">
                        <button
                          onClick={() => handleEdit(course)}
                          className="text-gray-400 hover:text-blue-500 inline-flex items-center transition-colors duration-200"
                          title="تعديل"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                      </PermissionGuard>
                      <PermissionGuard permission="edit_course">
                        <button
                          onClick={() => handlePublishToggle(course)}
                          className={`${
                            course.status === 'published'
                              ? 'hover:text-gray-600'
                              : 'hover:text-gray-600'
                          } inline-flex items-center mr-2 transition-colors duration-200`}
                          title={course.status === 'published' ? 'إلغاء النشر' : 'نشر الدورة'}
                        >
                          {course.status === 'published' ? (
                            <EyeIcon className="h-5 w-5 text-green-500" />
                          ) : (
                            <EyeOffIcon className="h-5 w-5 text-gray-400" />
                          )}
                        </button>
                      </PermissionGuard>
                      <PermissionGuard permission="delete_course">
                        <button
                          onClick={() => handleDelete(course)}
                          className="text-red-500 hover:text-red-700 inline-flex items-center mr-2 transition-colors duration-200"
                          title="حذف"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </PermissionGuard>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* نافذة تأكيد الحذف */}
      <DeleteConfirmModal
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        itemType="الدورة"
        itemTitle={selectedCourse?.name}
      />

      {/* نافذة تأكيد النشر/إلغاء النشر */}
      <PublishConfirmModal
        isOpen={publishDialogOpen}
        onClose={() => setPublishDialogOpen(false)}
        onConfirm={handlePublishConfirm}
        isPublished={selectedCourse?.status === 'published'}
        itemTitle={selectedCourse?.name}
        noteClass="text-yellow-600 dark:text-yellow-400"
      />
    </div>
  );
};

export default CoursesListPage;
